<template>
  <ais-current-refinements v-resize="handleResize"
                           id="filter-options">
    <template v-slot="{ items: refinements }">
      <!-- Mobile tabs -->
      <div v-if="mobile"
           class="sections-tabs-wrapper">
        <div class="sections-tabs"
             :class="{ 'sections-tabs--active': !!visibleSection }">
          <div v-for="section in sections"
               :key="section"
               class="sections-tab"
               :class="{ 'sections-tab--active': visibleSection === section }"
               @click="toggleVisibleSection(section)">
            {{ section }}
            <span v-show="areSelectedRefinementsCountVisible(section, refinements)">
              &nbsp;({{ removableRefinements(refinements).length }})
            </span>
            <i :class="['fas', isSectionVisible(section) ? 'fa-caret-down' : 'fa-caret-right']"/>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div v-show="isSectionVisible('filters')"
           class="filter-options">
        <ais-dynamic-widgets>

          <!-- Mobile top checkbox filters -->
          <div v-if="mobile"
              class="top-toggle-filters">
            <ais-toggle-refinement v-for="filter in toggleFilters"
                                   :key="filter.value"
                                   :attribute="filter.value">

              <template v-slot="{ value, refine }">
                <sn-checkbox :value="value.isRefined"
                             :label="getToggleFilterName(filter)"
                             @input="refine(value)"/>
              </template>
            </ais-toggle-refinement>
          </div>

          <!-- Filters sections -->
          <ais-panel v-for="filter in displayedFilters"
                     :key="filter.name"
                     class="filter">

            <!-- Filters section header -->
            <div class="filter-header"
                 :class="{ active: filter.active }"
                 role="button"
                 @click="toggleFilter(filter)">
              <span>
                {{ getMainFilterName(filter.name) }}
              </span>
              <img v-if="filter.active" src="@/assets/images/minus-bold.svg" />
              <img v-else src="@/assets/images/plus-thin.svg" />
            </div>

            <!-- Filters section body -->
            <template v-if="['multiselection', 'checkbox'].includes(filter.type)">
              <ais-refinement-list v-show="filter.active"
                                   :attribute="filter.name"
                                   class="menu"
                                   :limit="maxMobileFilterValueShow"
                                   :show-more-limit="maxMobileFilterValueShowMore"
                                   :sort-by="facetsSorting"
                                   show-more>
                <template #default="{ items, refine, canToggleShowMore, toggleShowMore, isShowingMore }">
                  <sn-checkbox v-for="item in items"
                               :key="item.value"
                               :value="item.isRefined"
                               @input="refine(item.value)">
                    <render-jsx :content="getFilterName(item.value, item.count)"/>
                  </sn-checkbox>
                  <div v-if="canToggleShowMore"
                       class="show-more"
                       @click="toggleShowMore">
                    {{ isShowingMore ? 'LESS' : 'MORE' }}
                    <img v-show="isShowingMore"
                         src="@/assets/images/minus-bold.svg"
                         class="show-more-img"/>
                    <img v-show="!isShowingMore"
                         src="@/assets/images/plus-bold.svg"
                         class="show-more-img"/>
                  </div>
                </template>
              </ais-refinement-list>
            </template>

            <template v-if="filter.type === 'multiselection_tree'">
              <ais-hierarchical-menu v-show="filter.active"
                                     class="menu multiselection-tree"
                                     :limit="100"
                                     :root-path="getHierarchicalMenuRootPath(filter)"
                                     :attributes="filter.values.attributes"
                                     :transform-items="transformHierarchicalMenuCategoryItems">
                <template v-slot="{ items, refine, createURL, canRefine }">
                  <div v-if="canRefine" class="menu-wrapper">
                    <div class="menu-top">
                      <filters-multiselection-tree-option v-for="item in items"
                                                          :key="item.value"
                                                          :filter="filter"
                                                          :option="item"
                                                          mobile
                                                          :createURL="createURL"
                                                          @select="refine($event)">

                        <template  #default="{ option }">
                          <sn-checkbox :value="isHierarchicalMenuOptionRefined(filter, option)"
                                       @input="refine(option.value)">
                            <render-jsx :content="getFilterName(option.label, option.count)"/>
                          </sn-checkbox>
                        </template>
                      </filters-multiselection-tree-option>
                    </div>
                  </div>
                </template>
              </ais-hierarchical-menu>
            </template>

            <template v-if="filter.type === 'range'">
              <ais-range-input v-show="filter.active"
                               :attribute="filter.name"
                               class="menu2">
                <template v-slot="{ currentRefinement, range, refine }">
                  <div class="slider-container">
                    <client-only>
                      <vue-slider :min="getMinRangeValue(range.min, filter.values.interval)"
                                  :max="getMaxRangeValue(range.max, filter.values.interval)"
                                  :interval="filter.values.interval"
                                  :tooltip-formatter="filter.name === 'price' ? '${value}' : '{value}'"
                                  :value="toValue(filter, currentRefinement, range)"
                                  @change="rangeRangeInputDebounce(refine, $event, range, currentRefinement)"/>
                    </client-only>
                  </div>

                  <div class="range-container">
                    <div class="range">
                      <p>From</p>

                      <div :class="{ 'currency-input-wrapper': filter.name === 'price' }">
                        <input :value="toValue(filter, currentRefinement, range)[0]"
                               type="number"
                               :min="getMinRangeValue(range.min, filter.values.interval)"
                               :max="getMaxRangeValue(range.max, filter.values.interval)"
                               @input="rangeRangeInputDebounce(
                                 refine,
                                 [parseFloat($event.target.value), filter.to],
                                 range,
                                 currentRefinement
                               )"/>
                      </div>
                    </div>

                    <div class="range">
                      <p>To</p>

                      <div :class="{ 'currency-input-wrapper': filter.name === 'price' }">
                        <input :value="toValue(filter, currentRefinement, range)[1]"
                               type="number"
                               :min="getMinRangeValue(range.min, filter.values.interval)"
                               :max="getMaxRangeValue(range.max, filter.values.interval)"
                               @input="rangeRangeInputDebounce(
                                 refine,
                                 [filter.from, parseFloat($event.target.value)],
                                 range,
                                 currentRefinement
                               )"/>
                      </div>
                    </div>
                  </div>
                </template>
              </ais-range-input>
            </template>
          </ais-panel>
        </ais-dynamic-widgets>
      </div>

      <!-- Sort -->
      <products-listing-filters-sort v-show="mobile && isSectionVisible('sort')"
                                     :index-name="indexName"/>

      <div v-show="!!visibleSection"
           class="action-wrapper">
        <button class="btn medium"
                @click="applyFilters">
          APPLY FILTERS
        </button>

        <ais-clear-refinements v-if="mobile"
                               class="filter-options__clear-filters-btn">
          <template v-slot="{ canRefine, refine, createURL }">
            <a v-if="canRefine && removableRefinements(refinements).length > 0"
               :href="createURL()"
               @click.prevent="refine(), $emit('applyFilters')">
              Clear Filters
            </a>
            <div v-else/>
          </template>
        </ais-clear-refinements>
      </div>
    </template>
  </ais-current-refinements>
</template>

<script lang="jsx">
  import {
    AisClearRefinements,
    AisCurrentRefinements,
    AisDynamicWidgets,
    AisHierarchicalMenu,
    AisPanel,
    AisRangeInput,
    AisRefinementList
  } from 'vue-instantsearch'

  import FiltersMultiselectionTreeOption from '@/../shared/components/FiltersMultiselectionTreeOption'
  import RenderJsx from '@/../shared/components/RenderJsx'
  import SnCheckbox from '@/../shared/components/SnCheckbox'
  import ProductsListingFiltersSort from '@/components/products/listing/filters/ProductsListingFiltersSort'

  import { debounce, cloneDeep, get } from 'lodash'
  import { getValueFromPermalink } from '@/../shared/utils/utils'
  import { mapGetters } from 'vuex'

  import ProductsListingToggleFiltersMixin from '@/mixins/products/listing/productsListingToggleFilters'
  import ProductsListingRemovableRefinementsMixin from '@/mixins/products/listing/productsListingRemovableRefinements'

  import '@/assets/stylesheets/vue-slider-component.scss'

  export default {
    name: 'FilterOptions',
    components: {
      AisClearRefinements,
      AisCurrentRefinements,
      AisHierarchicalMenu,
      AisPanel,
      AisRangeInput,
      AisRefinementList,
      FiltersMultiselectionTreeOption,
      ProductsListingFiltersSort,
      RenderJsx,
      SnCheckbox,
      AisDynamicWidgets,
      VueSlider: () => (process.client ? import('vue-slider-component') : null)
    },
    mixins: [
      ProductsListingToggleFiltersMixin,
      ProductsListingRemovableRefinementsMixin
    ],
    props: {
      indexName: {
        type: String,
        required: true
      },
      filters: {
        type: Array,
        default: () => []
      },
      uiData: {
        type: Object,
        default: null
      },
      categories: {
        type: Array,
        default: () => []
      }
    },
    data () {
      return {
        innerFilters: [],
        maxMobileFilterValueShow: 6,
        maxMobileFilterValueShowMore: 999,
        facetsSorting: ['name:asc'],
        noFiltersText: 'There are no filters available for the current search',
        allowedFilterTypes: ['checkbox', 'multiselection', 'multiselection_tree', 'range'],
        visibleSection: null,
        sections: ['filters', 'sort'],
        mobile: false
      }
    },
    watch: {
      filters(newFilters) {
        this.updateInnerFilters(newFilters)
      }
    },
    mounted() {
      this.handleResize()
      this.updateInnerFilters(this.filters)
    },
    computed: {
      ...mapGetters('designerTiers', [
        'getTierId'
      ]),
      displayedFilters() {
        const toggleFiltersNames = this.toggleFilters.map(filter => filter.value)

        return this.innerFilters.filter(filter => {
          return (
            this.allowedFilterTypes.includes(filter.type) &&
            !toggleFiltersNames.includes(filter.name) &&
            !this.hiddenFiltersNames.test(filter.name)
          )
        })
      },
      isCategoryPage() {
        return this.$route.name === 'categories'
      },
      // Returns RegExp to match the price filters to be hidden - e.g., "designer_tier1_price", "designer_tier3_price",
      // "price" and all other designer_tierX_price filters if tier 2 is active, or all "designer_tierX_price" filters
      // if no tier is active.
      hiddenFiltersNames () {
        if (!this.getTierId) return /^designer_tier\d+_price$/i

        return new RegExp(`^(?:designer_tier(?!(?:${this.getTierId}_))\\d+_)?price$`, 'i')
      }
    },
    methods: {
      transformHierarchicalMenuCategoryItems (items) {
        if (!items) return null

        return items.map(item => {
          const category = this.categories.find(category => category.permalink === item.value.replace(/ > /g, '/'))

          return {
            ...item,
            label: category?.name || item.label,
            data: this.transformHierarchicalMenuCategoryItems(item.data)
          }
        })
      },
      applyFilters () {
        this.visibleSection = null
        this.$emit('applyFilters')
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      toggleVisibleSection (section) {
        this.visibleSection === section ? this.visibleSection = null : this.visibleSection = section
      },
      isSectionVisible (section) {
        return !this.mobile || this.visibleSection === section
      },
      handleResize (e) {
        this.mobile = window.innerWidth <= 1243
      },
      updateInnerFilters(filters) {
        this.innerFilters = cloneDeep(filters)
      },
      getHierarchicalMenuRootPath({ name }) {
        if (this.$route.name === 'holiday-collection') return 'holiday'
        if (name !== 'category' || !this.isCategoryPage) return

        return getValueFromPermalink(this.$route.params.name)
      },
      getMinRangeValue(value, interval) {
        return this.getRangeRoundedValue(value, interval, Math.floor)
      },
      getMaxRangeValue(value, interval) {
        return this.getRangeRoundedValue(value, interval, Math.ceil)
      },
      getRangeRoundedValue(value, interval, quotientFunction) {
        value = parseFloat(value)
        interval = parseFloat(interval)

        return quotientFunction(value / interval) * interval
      },
      toValue(filter, refinement, range) {
        let currentRefinement = refinement
        const tmpRefinementString = get(this.uiData, ['uiState', this.indexName, 'range', filter.name])

        if (tmpRefinementString) {
          const [min, max] = tmpRefinementString.split(':').map(e => +e)
          currentRefinement = { min, max }
        }

        return [
          this.toSingleValue(filter.from, currentRefinement.min, range.min),
          this.toSingleValue(filter.to, currentRefinement.max, range.max)
        ]
      },
      toSingleValue(filter, refinement, range) {
        let value = range

        if (typeof filter === 'number') {
          value = filter
        } else if (typeof refinement === 'number') {
          value = refinement
        }

        return value
      },
      rangeRangeInputDebounce: debounce(function (refine, value, range, currentRefinement) {
        this.rangeRefine(refine, { from: value[0], to: value[1] }, range, currentRefinement)
      }, 1000),
      getFilterName(value, count) {
        if (/^true$/i.test(value)) value = 'Yes'
        if (/^false$/i.test(value)) value = 'No'

        return (
          <span>
            {value} <span class="filter-options__products-count">({count})</span>
          </span>
        )
      },
      getMainFilterName(value) {
        return value
          .replace(/^properties\.filter_/, '')
          .replace('supplier.name', 'brand')
          .replace(/designer_tier\d+_price/, 'price')
          .replace('on_sale', 'sale')
          .replace(/\.lvl\d+/, '')
          .replace(/_/g, ' ')
      },
      toggleFilter(filter) {
        let currentFilterState = filter.active
        filter.active = !currentFilterState
      },
      rangeRefine(refine, filter, range, currentRefinement) {
        let value = this.toValue(filter, currentRefinement, range)

        refine({
          min: Math.max(value[0], range.min),
          max: Math.min(value[1], range.max)
        })
      },
      areSelectedRefinementsCountVisible (section, refinements) {
        return (
          this.mobile &&
          this.removableRefinements(refinements).length &&
          section === 'filters' &&
          this.visibleSection !== 'filters'
        )
      },
      isHierarchicalMenuOptionRefined(filter, option) {
        const attribute = filter.values.attributes[0]
        const refinement = get(this.uiData, ['uiState', this.indexName, 'hierarchicalMenu', attribute], []).join(' > ')
        if (!!refinement) return refinement.startsWith(option.value)

        return option.isRefined
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/variables.scss';

  .sections-tabs-wrapper {
    width: 100%;
    height: 50px;
    position: relative;
    overflow: visible;
  }

  .sections-tabs {
    display: grid;
    align-items: center;
    justify-content: center;
    grid-template-columns: 1fr 1fr;
    font-family: 'MotivaSans Light';
    font-size: 1.5rem;
    color: $gunmetal;
    text-transform: uppercase;
    width: 100vw;
    position: absolute;
    top: 0;
    left: 0;

    &--active {
      border-bottom: 1px solid $gunmetal;
    }
  }

  .sections-tab {
    border: 1px solid $border-line;
    padding: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-left: none;

    &:first-child {
      border-left: none;
    }

    &:last-child {
      border-right: none;
    }

    &:hover, &--active {
      background: $off-white;
    }

    &--active {
      font-family: 'MotivaSans SemiBold';
    }

    i {
      display: inline-block;
      margin-left: 10px;
    }
  }

  .currency-input-wrapper {
    position: relative;

    input {
      padding-left: 20px !important;
    }

    &::before {
      content: '$';
      position: absolute;
      color: $sn-search-color;
      font-size: 1.6rem;
      top: 4px;
      left: 10px;
    }
  }

  ::v-deep .ais-DynamicWidgets-widget {
    &:not(:first-child) {
      border-top: 1px solid $silver-gray;
    }

    &:has(> .ais-Panel--noRefinement) {
      display: none;
    }
  }

  .filter-options {
    background: $snow-white;
    height: fit-content;

    .filter {
      &:hover {
        background: $pale-gray;
      }

      .filter-header {
        display: grid;
        grid-gap: 10px;
        grid-template-columns: 1fr auto;
        font-size: 1.5rem;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.38;
        letter-spacing: 0.38px;
        padding: 19px 15px 19px 25px;
        color: $secondary-color;
        text-transform: uppercase;
        cursor: pointer;
        font-family: 'MotivaSans Light';
        color: $gunmetal;
        align-items: center;

        &.active {
          font-family: 'MotivaSans SemiBold';
        }
      }
      .menu {
        display: grid;
        grid-row-gap: 9px;
        padding: 0 40px 19px;

        .show-more {
          font-size: 1.4rem;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.36;
          letter-spacing: 0.34px;
          text-align: center;
          color: $gunmetal;
          cursor: pointer;
          padding-top: 12px;
          padding-bottom: 12px;
          font-family: 'MotivaSans Light';
        }

        .show-more-img {
          height: 10px;
          width: 10px;
        }
      }

      .menu2 {
        padding: 19px 40px 25px 40px;

        ::v-deep .slider-container {
          .vue-slider-process,
          .vue-slider-dot-handle,
          .vue-slider-dot-tooltip-inner {
            background-color: $color-gray-dark;
          }

          .vue-slider-dot {
            width: 24px !important;
            height: 24px !important;
          }
        }

        .range-container {
          display: grid;
          grid-template-columns: auto auto;
          grid-gap: 10px;
          justify-content: space-between;

          input {
            border: 1px solid #cccccc;
            padding-left: 7.5px;
            width: 100px;
          }
        }
      }

      .menu-top {
        display: grid;
        row-gap: 9px;
      }
    }

    ::v-deep .sn-checkbox {
      font-size: 1.3rem !important;
      font-family: 'MotivaSans Light';
      display: flex;
      align-items: center;
      padding-left: 28px;
      text-transform: capitalize;

      .checkmark {
        width: 15px;
        height: 15px;
        top: 5px;
        background: transparent;

        &::after {
          border-color: $color-black;
          left: 5px;
          top: 2px;
          width: 4px;
          height: 8px;
          border-width: 0 2px 2px 0;
        }
      }

      input {
        &:checked, &:hover {
          ~ .checkmark {
            border-radius: 0;
            background: transparent;
          }
        }
      }
    }

    &__clear-filters-btn {
      margin: 10px auto 0;
      color: $dark-chestnut-brown;
      font-family: 'MotivaSans Light';
      font-size: 1.2rem;
      cursor: pointer;
      text-decoration: underline;
      width: fit-content;
    }

    &__products-count {
      color: $neutral-gray;
    }
  }

  .top-toggle-filters {
    display: grid;
    row-gap: 25px;
    padding: 19px 15px 19px 25px;

    ::v-deep .sn-checkbox {
      font-size: 1.5rem !important;
    }
  }

  .action-wrapper {
    border-bottom: 1px solid $gunmetal;
    padding: 24px 0;

    button {
      font-family: 'MotivaSans Light';
      background: $color-olive-green;
      color: $color-white;
      border: none;
    }
  }

  ::v-deep .filter-multiselection-tree-option {
    &__children {
      margin-top: 5px;
      padding-left: 30px;
    }

    .btn {
      color: $color-white;
      border: 1px solid $gunmetal;
      background-color: $gunmetal;

      &:hover {
        color: $gunmetal;
        border: 1px solid $gunmetal;
        background-color: $color-white;
      }

      &.inverted {
        &, &:visited {
          color: $gunmetal;
          border: 1px solid $gunmetal;
          background-color: $color-white;
        }

        &:hover {
          color: $color-white;
          background-color: $gunmetal;
        }
      }
    }
  }

  @media screen and (max-width: 1366px) {
    .sections-tabs {
      left: -60px;
    }
  }

  @media screen and(max-width: $products-filters-mobile-max) {
    .filter-options {
      position: relative;
      z-index: 1;

      &, &::before {
        background: none !important;
      }

      &::before {
        content: '';
        display: block;
        background: $snow-white;
        width: 100vw;
        position: absolute;
        top: 0;
        left: calc(50% - 30px);
        transform: translateX(-50%);
        height: 100%;
        z-index: -1;
      }
    }

    .filter-header {
      grid-template-columns: auto 1fr !important;
      column-gap: 15px !important;

      img {
        order: -1;
      }
    }
  }

  @media screen and(max-width: 1024px) {
    .sections-tabs {
      margin-left: -10px;
      left: 0;
    }

    .filter-options {
      &::before {
        left: calc(50% - 5px);
      }
    }
  }
</style>
